import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useToast } from '@bonusly/design-system';
import { useHttpClient } from '@bonusly/http-client';
import { useCurrentUser } from '@bonusly/who-am-i';

import { QueryKey } from '../../../../../types/growth-query-key';
import { MeetingDTO } from '../../../lib/entities/meeting';

interface UseCancelMeetingModalArgs {
	meeting: MeetingDTO;
	guestId: string;
	onClose: VoidFunction;
}
export const useCancelMeetingModal = ({ meeting, guestId, onClose }: UseCancelMeetingModalArgs) => {
	const { t } = useTranslation('one_on_ones');
	const http = useHttpClient('v2');
	const toast = useToast();
	const queryClient = useQueryClient();
	const user = useCurrentUser();

	const params = { guest_id: guestId };

	// Handle middle-manager meeting cancelation
	if (guestId === user.manager_id) {
		params.guest_id = user.id;
	}

	const deleteMeeting = useMutation(async () => http.delete(`one_on_ones/meetings/${meeting.id}`), {
		onSuccess() {
			queryClient.invalidateQueries([QueryKey.CurrentOneOnOne]);
			queryClient.invalidateQueries([QueryKey.Meetings]);
			queryClient.invalidateQueries([QueryKey.Meeting]);
			queryClient.invalidateQueries([QueryKey.Event, meeting.event?.id]);
			queryClient.invalidateQueries([QueryKey.MeetingTimes, { eventId: meeting.event?.id }]);
			queryClient.invalidateQueries([QueryKey.MeetingTimes, { userId: guestId }]);
			queryClient.invalidateQueries([QueryKey.TeamActivityDetails, user.id]);

			toast({
				status: 'success',
				title: t('meeting.delete_modal.toast_success'),
			});
		},
		onError: () => {
			toast({
				status: 'error',
				title: t('meeting.delete_modal.toast_error'),
			});
		},
		onSettled: () => {
			onClose();
		},
	});

	const onCancelMeeting = () => {
		deleteMeeting.mutate();
	};

	return {
		isSubmitting: deleteMeeting.isLoading,
		onCancelMeeting,
	};
};
